import React from 'react';
import {Icon, Text} from '@albert-io/atomic';

import type {CleverClassResult, CleverSyncResults} from './util';
import {isPendingUnlicensedResult} from './util';
import './sync-result.scss';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';

interface Props {
  result: CleverClassResult;
}

export const PENDING_UNLICENSED_NOTE =
  'Classes marked "Not licensed" were imported while you are pending approval at their school, so they do not use license seats. Once approved, turn on licensing from each class’s settings.';

const SyncResults = ({
  syncResults,
  className
}: {
  syncResults?: CleverSyncResults;
} & PropsWithClassNameOptional) => {
  if (!syncResults?.success) {
    return null;
  }
  const hasPendingUnlicensed = syncResults.results.some(isPendingUnlicensedResult);
  return (
    <div className={`w-full overflow-y-auto ${className}`}>
      {syncResults.results.map((r) => (
        <ClassroomSyncResult key={r.classroom_name} result={r} />
      ))}
      {hasPendingUnlicensed && (
        <Text
          as='p'
          className='mt-x1 text-left'
          color='secondary'
          data-testid='clever-sync-results__pending-unlicensed-note'
          size='s'
        >
          {PENDING_UNLICENSED_NOTE}
        </Text>
      )}
    </div>
  );
};

const ClassroomSyncResult = ({result}: Props) => {
  const messageDetails = getMessageDetails(result);
  return (
    <div className='mb-x2 flex w-full justify-between'>
      <Text
        className='overflow-hidden text-left text-ellipsis whitespace-nowrap'
        title={result.classroom_name}
      >
        {result.classroom_name}
      </Text>
      <div className='center-padding mx-x1' />
      <div className='flex items-center'>
        <Text color={messageDetails.color} className='mr-x1 whitespace-nowrap'>
          {messageDetails.message}
        </Text>
        <Icon icon={messageDetails.icon} color={messageDetails.color} />
      </div>
    </div>
  );
};

const getMessageDetails = (
  result: CleverClassResult
): {
  icon: IconProp;
  color: 'negative' | 'positive' | 'tertiary' | 'warning';
  message: string;
} => {
  if (!result.success) {
    if (
      result.message ===
      "Class was not imported because it was not linked to a school found in Albert's system.\n"
    ) {
      return {
        icon: 'xmark',
        color: 'negative',
        message: 'No matching school in Albert'
      };
    }
    const badEmailRegex = /^The provided email '([^']+)' failed email domain validation\.$/;
    const badEmailMatch = result.message.match(badEmailRegex);
    if (badEmailMatch) {
      return {
        icon: 'xmark',
        color: 'negative',
        message: `Contains non-school emails (${badEmailMatch[1]}).`
      };
    }
    return {
      icon: 'xmark',
      color: 'negative',
      message: 'Unknown Error'
    };
  }
  if (result.status === 'archived') {
    return {
      icon: ['far', 'box-archive'],
      color: 'tertiary',
      message: 'Archived'
    };
  }
  if (isPendingUnlicensedResult(result)) {
    return {
      icon: ['far', 'triangle-exclamation'],
      color: 'warning',
      message: 'Not licensed'
    };
  }
  return {
    icon: 'check',
    message: 'Synced',
    color: 'positive'
  };
};

export default SyncResults;
